import type { ReactNode } from "react";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from "../../../../atoms/ContextMenu";
import { Skeleton } from "../../../../atoms/Skeleton";
import { Text } from "../../../../atoms/Text";
import { cn } from "../../../../lib/cn";
import {
  compactRowBelowClass,
  compactValueBelowClass,
  hideBelowClass,
  hideBelowWidthClass,
} from "../collapse";
import { createActivationKeyHandler } from "../rowInteraction";
import {
  TITLE_FLEX,
  type LedgerCells,
  type LedgerCollapse,
  type LedgerColumn,
  type LedgerRowMenu,
} from "../types";
import { LedgerRowText } from "./LedgerRowText";

export interface LedgerRowProps {
  columns: readonly LedgerColumn[];
  collapse: LedgerCollapse;
  /** Cell content by column key; keys with no column are ignored. */
  cells?: LedgerCells;
  lead?: ReactNode;
  title: ReactNode;
  titleTag?: ReactNode;
  /** Everything the hidden columns carried, for widths below the collapse point. */
  meta?: ReactNode;
  value?: ReactNode;
  showValues?: boolean;
  /**
   * The cell content is still loading: columns render placeholders, and so
   * does the Value box when no value has arrived either. The title, lead and
   * any value already known render as normal.
   */
  pending?: boolean;
  /** Cancelled rows dim; nothing else does. */
  dimmed?: boolean;
  hoverCard?: ReactNode;
  /** Visible controls rendered beside the row's primary action. */
  actions?: ReactNode;
  /** Omit for no context menu at all — an empty menu is worse than none. */
  menu?: LedgerRowMenu;
  onClick?: () => void;
  className?: string;
  "data-testid"?: string;
  /** Machine-readable status attribute on the row root (e2e contract). */
  "data-status"?: string;
  "aria-label"?: string;
}

function LedgerCell({
  column,
  collapse,
  content,
  pending,
}: {
  column: LedgerColumn;
  collapse: LedgerCollapse;
  content: ReactNode;
  pending: boolean;
}) {
  const collapseClass =
    column.hideBelow === undefined
      ? hideBelowClass(collapse)
      : hideBelowWidthClass(collapse.container, column.hideBelow);
  return (
    <div
      className={cn(
        "min-w-0",
        collapseClass,
        column.align === "center" && "text-center",
        column.align === "end" && "text-right",
      )}
      style={{ flex: `${column.flex} 1 0` }}
    >
      {pending ? (
        <Skeleton variant="text" width="70%" />
      ) : (
        <Text
          as="div"
          variant="inherit"
          color="secondary"
          className="truncate text-(length:--ledger-text-cell) leading-[1.66]"
        >
          {content}
        </Text>
      )}
    </div>
  );
}

function LedgerValue({ props }: { props: LedgerRowProps }) {
  if (props.showValues === false) {
    return null;
  }
  return (
    <div
      className={cn(
        "w-(--ledger-value-width) shrink-0 text-right",
        compactValueBelowClass(props.collapse),
      )}
    >
      {props.pending && props.value == null ? (
        <Skeleton variant="text" width={64} className="ml-auto" />
      ) : (
        props.value
      )}
    </div>
  );
}

function LedgerPrimaryAction({ props }: { props: LedgerRowProps }) {
  const keyboardProps = props.onClick
    ? {
        onKeyDown: createActivationKeyHandler(props.onClick),
        role: "button" as const,
        tabIndex: 0,
      }
    : undefined;
  return (
    <div
      {...keyboardProps}
      aria-label={props["aria-label"]}
      className={cn(
        "flex min-w-0 flex-1 items-center gap-3 py-1.5",
        "focus-visible:-outline-offset-2 focus-visible:outline-2 focus-visible:outline-border-focused",
        compactRowBelowClass(props.collapse),
      )}
    >
      <div className="min-w-0" style={{ flex: `${TITLE_FLEX} 1 0` }}>
        <LedgerRowText
          lead={props.lead}
          title={props.title}
          titleTag={props.titleTag}
          meta={props.meta}
          collapse={props.collapse}
          hoverCard={props.hoverCard}
        />
      </div>
      {props.columns.map((column) => (
        <LedgerCell
          key={column.key}
          column={column}
          collapse={props.collapse}
          content={props.cells?.[column.key] ?? ""}
          pending={props.pending ?? false}
        />
      ))}
      <LedgerValue props={props} />
    </div>
  );
}

function LedgerRowBody({ props }: { props: LedgerRowProps }) {
  return (
    <div
      aria-busy={props.pending || undefined}
      data-testid={props["data-testid"]}
      data-status={props["data-status"]}
      onClick={props.onClick}
      className={cn(
        "group/ledger-row flex min-h-(--ledger-row-min-height) items-stretch gap-3 border-b border-border-default px-4 hover:bg-surface-raised",
        props.dimmed && "text-content-secondary",
        props.onClick !== undefined && "cursor-pointer",
        props.className,
      )}
    >
      <LedgerPrimaryAction props={props} />
      {props.actions != null ? (
        <div
          className="flex w-9 shrink-0 items-center justify-end"
          onClick={(event) => {
            event.stopPropagation();
          }}
        >
          {props.actions}
        </div>
      ) : null}
    </div>
  );
}

/** Generic compact row configured by domain rows and feature adapters. */
export const LedgerRow = (props: LedgerRowProps) => {
  const row = <LedgerRowBody props={props} />;
  if (props.menu === undefined) {
    return row;
  }
  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>{row}</ContextMenuTrigger>
      <ContextMenuContent>
        <ContextMenuItem
          disabled={props.menu.disabled}
          data-testid={props.menu["data-testid"]}
          onSelect={props.menu.onSelect}
        >
          {props.menu.label}
        </ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  );
};
